import type { Grid } from "../core/Grid.ts";
import type { Pathfinding, PathGrid } from "../core/Pathfinding.ts";
import type { PathRegistry } from "../core/PathRegistry.ts";
import { CellType } from "../core/types.ts";
import type { Vec2 } from "../core/types.ts";
import {
  type SpawnCell,
  type SpawnLayout,
  scanSpawnLayout,
} from "../core/SpawnModel.ts";
import { DEFAULT_SPAWN_POLICY, GAME, type SpawnPolicy } from "../core/config.ts";

/** DD-14.7-4: Adaptive trigger threshold */
const KDS_THRESHOLD = 0.25;
const MIN_WAVE_FOR_ADAPTIVE = 12;
const MIN_WAVES_SINCE_LAST_BREACH = 4;
/** BL-146 / D-248 — the boss-breach roll is an ACT II+ event ("she breaches the
 *  walls" begins with the Act II trespass), so a boss can never crack Act I's
 *  one clean lane. Pulled up from the old flat `wave < 10` floor. */
const BOSS_BREACH_MIN_WAVE = GAME.WAVES_ACT_I + 1;

/** BL-146 — which wall a spawn cell sits on (the static layout is top/bottom).
 *  The pincer converges from OPPOSITE walls, so eruption side-selection reads
 *  this to alternate. Interior is reserved for a future right-side gate. */
type SpawnWall = "top" | "bottom" | "interior";
/* BL-145 / D-247: the CR-2 eruption cap (`maxActiveSpawns`, =4 → all 4
 * secondary spawns can be live late-game, 4 secondary + 1 primary = up to 5
 * simultaneous streams) and the scripted ladder (`scriptedBreachWaves`,
 * =[8,16,24,30] → threat ramps predictably, all spawns activate by ~W30) now
 * live on `SpawnPolicy` (`config.ts`), default-identical. */

/** AC-10 / OQ-3 — the inclusive wave band of the act a wave belongs to. The
 *  seeded ladder jitter is clamped into this band so a breach can move WITHIN
 *  its act but never across an act boundary (see {@link BreachSystem.jitterLadder}). */
function actBandOf(wave: number): [number, number] {
  if (wave <= GAME.WAVES_ACT_I) return [1, GAME.WAVES_ACT_I];
  if (wave <= GAME.WAVES_ACT_II) return [GAME.WAVES_ACT_I + 1, GAME.WAVES_ACT_II];
  return [GAME.WAVES_ACT_II + 1, GAME.WAVES_ACT_III];
}

/** BL-147: the runtime breach-registry entry — a spawn cell plus its erupted
 *  flag. Extends the shared {@link SpawnCell} so the `{ col, row }` shape is
 *  the single one from `core/SpawnModel`. */
export interface DormantSpawn extends SpawnCell {
  active: boolean;
}

export interface BreachResult {
  spawn: DormantSpawn;
  path: Vec2[];
  destroyedCells: Vec2[];
}

/**
 * CR-27b: Owns dormant-spawn registry, breach triggers, path computation,
 * and tower-destruction queue. Pure logic, no rendering.
 */
export class BreachSystem {
  private dormantSpawns: DormantSpawn[] = [];
  private grid: Grid;
  private pathfinding: Pathfinding;
  private pathRegistry: PathRegistry | null = null;
  private lastBreachWave = -Infinity;
  private rng: () => number;
  private deferredBreachPending = false;
  /** BL-145: the data-driven placement policy (the scripted ladder + the
   *  active-spawn cap). Defaults to the byte-identical literals. */
  private policy: SpawnPolicy;
  /** BL-146: the wall the most recent eruption came from, so the next pick can
   *  prefer the OPPOSITE wall (the converging pincer). Null until the first
   *  eruption. */
  private lastEruptedWall: SpawnWall | null = null;
  /** BL-146: set once the Act III pincer has erupted — releases the
   *  {@link SpawnPolicy.pincerReserve} so post-pincer triggers see the full cap. */
  private pincerFired = false;

  // Adaptive tracking (CR-27c)
  private killDistances: number[] = [];
  private primaryPathLength = 0;
  /** AC-10 / OQ-3 — this run's scripted ladder and pincer waves, after the
   *  seeded jitter. Equal to the policy constants when the jitter knob is 0
   *  (the default), so the shipped ladder is unchanged until it is tuned. */
  private scriptedWaves: readonly number[] = [];
  private pincerWaves: readonly number[] = [];

  constructor(
    grid: Grid,
    pathfinding: Pathfinding,
    rng: () => number,
    policy: SpawnPolicy = DEFAULT_SPAWN_POLICY,
  ) {
    this.grid = grid;
    this.pathfinding = pathfinding;
    this.rng = rng;
    this.policy = policy;
    this.rollLadder();
  }

  /** AC-10 / OQ-3 — (re)draw this run's scripted ladder. Called from the
   *  constructor and from {@link reset}, i.e. exactly once per run, so the
   *  ladder is stable for the whole run and a fresh run re-draws it. With the
   *  jitter at its default 0 this takes NO draw from the breach stream, which
   *  is what keeps the existing golden masters byte-identical. */
  private rollLadder(): void {
    this.scriptedWaves = this.jitterLadder(this.policy.scriptedBreachWaves);
    this.pincerWaves = this.jitterLadder(this.policy.pincerWaves);
  }

  setPathRegistry(registry: PathRegistry): void {
    this.pathRegistry = registry;
  }

  /** Register dormant + hidden spawns from the run layout (or grid fallback).
   *  BL-147: the grid-rescan fallback is now the shared `scanSpawnLayout`. */
  init(layout?: SpawnLayout): void {
    const resolved = layout ?? scanSpawnLayout(this.grid);
    this.dormantSpawns = [];
    for (const pos of [...resolved.dormant, ...resolved.hidden]) {
      this.dormantSpawns.push({ col: pos.col, row: pos.row, active: false });
    }
  }

  /** F6: Reset for a new run — restore activated spawns back to DORMANT_SPAWN
   *  on the grid, clear adaptive tracker, clear lastBreachWave. */
  reset(): void {
    for (const s of this.dormantSpawns) {
      if (s.active) {
        // Unregister from PathRegistry before grid mutation
        this.pathRegistry?.unregister(`breach:${s.col},${s.row}`);
        // Roll the cell type back so the grid reflects a fresh map.
        this.grid.setCell(s.col, s.row, CellType.DORMANT_SPAWN);
      }
      s.active = false;
    }
    this.killDistances.length = 0;
    this.lastBreachWave = -Infinity;
    this.deferredBreachPending = false;
    this.lastEruptedWall = null;
    this.pincerFired = false;
    // AC-10 / OQ-3 — a new run gets a newly-drawn ladder, so the flank does not
    // open on the same waves every run (O-5's Q-d complaint).
    this.rollLadder();
    // primaryPathLength gets re-set after recalculatePath runs.
  }

  /** Set primary path length for adaptive trigger math */
  setPrimaryPathLength(len: number): void {
    this.primaryPathLength = len;
  }

  /** CR-27c: Record a kill distance for adaptive breach tracking */
  recordKillDistance(dist: number): void {
    this.killDistances.push(dist);
    if (this.killDistances.length > 20) {
      this.killDistances.shift();
    }
  }

  /** Check if an adaptive breach should trigger */
  shouldAdaptiveBreach(wave: number): boolean {
    if (wave < MIN_WAVE_FOR_ADAPTIVE) return false;
    if (wave - this.lastBreachWave < MIN_WAVES_SINCE_LAST_BREACH) return false;
    if (this.killDistances.length < 20) return false;
    if (this.primaryPathLength <= 0) return false;
    if (!this.hasAvailableSpawn()) return false;

    const avgDist =
      this.killDistances.reduce((a, b) => a + b, 0) / this.killDistances.length;
    const kdsRatio = avgDist / this.primaryPathLength;
    return kdsRatio < KDS_THRESHOLD;
  }

  /** Check if scripted breach should fire (CR-2: the Act II flank ladder). */
  shouldScriptedBreach(wave: number): boolean {
    return this.scriptedWaves.includes(wave) && this.hasAvailableSpawn();
  }

  /** Boss breach roll (30% chance on boss waves, template-driven). BL-146: an
   *  Act II+ event so Act I's clean lane is never cracked by a boss roll. */
  shouldBossBreach(wave: number, isBossWave = false): boolean {
    if (wave < BOSS_BREACH_MIN_WAVE) return false;
    if (!isBossWave) return false;
    if (!this.hasAvailableSpawn()) return false;
    return this.rng() < 0.3;
  }

  /** BL-146 / D-248 — the Act III converging pincer fires on a `pincerWaves`
   *  wave when its reserved spawns are available. Uses the FULL cap (the
   *  reserve exists precisely so this trigger has material). */
  shouldPincerBreach(wave: number): boolean {
    return this.pincerWaves.includes(wave) && this.hasAvailableSpawn(true);
  }

  /** BL-146 — the active-spawn ceiling an eruption may fill. The ordinary
   *  triggers (scripted/adaptive/boss) leave `pincerReserve` spawns dormant so
   *  the Act III pincer is guaranteed ≥2 from different walls; the pincer (and
   *  everything after it fires) sees the full `maxActiveSpawns`. */
  private effectiveActiveCap(forPincer: boolean): number {
    if (
      forPincer ||
      this.pincerFired ||
      this.policy.pincerWaves.length === 0
    ) {
      return this.policy.maxActiveSpawns;
    }
    return Math.max(
      0,
      this.policy.maxActiveSpawns - this.policy.pincerReserve,
    );
  }

  hasAvailableSpawn(forPincer = false): boolean {
    // C2: cap-gated. Spawns never deactivate, so once the cap have erupted
    // there is no "available" spawn — without this gate the deferred breach +
    // "ERUPTION IMMINENT" banner looped every wave forever. BL-146: the cap is
    // reserve-aware (see effectiveActiveCap).
    return (
      this.getActiveSpawns().length < this.effectiveActiveCap(forPincer) &&
      this.dormantSpawns.some((s) => !s.active)
    );
  }

  hasDeferredBreach(): boolean {
    return this.deferredBreachPending && this.hasAvailableSpawn();
  }

  peekNextSpawn(): DormantSpawn | null {
    return this.pickNextSpawn();
  }

  /** BL-146 — which wall a spawn sits on (the static layout is top/bottom). */
  private wallOf(s: SpawnCell): SpawnWall {
    if (s.row === 0) return "top";
    if (s.row === this.grid.height - 1) return "bottom";
    return "interior";
  }

  /** BL-146 — choose the next spawn to erupt. Prefer a wall OPPOSITE the last
   *  eruption so successive breaches converge from two sides (the pincer); among
   *  equals, deterministically take the deepest (closest-to-core) cell so the
   *  flank reaches toward the Mainframe. Fully deterministic — adds no draw to
   *  the isolated breach stream (the geometry still varies per seed via the
   *  seeded static layout). */
  private pickNextSpawn(): DormantSpawn | null {
    const available = this.dormantSpawns.filter((s) => !s.active);
    if (available.length === 0) return null;
    if (this.lastEruptedWall) {
      const opposite = available.filter(
        (s) => this.wallOf(s) !== this.lastEruptedWall,
      );
      if (opposite.length > 0) return this.deepestSpawn(opposite);
    }
    return this.deepestSpawn(available);
  }

  /** Deterministic tiebreak: the cell nearest the core (largest col), then by
   *  row, then col — a stable total order so the pick never depends on RNG. */
  private deepestSpawn(pool: DormantSpawn[]): DormantSpawn {
    const ranked = [...pool].sort(
      (a, b) => b.col - a.col || a.row - b.row || a.col - b.col,
    );
    // AC-10 / OQ-3 — seeded depth pick. `spawnPickSeededDepth` <= 1 keeps the
    // pre-lever behaviour (always the single deepest cell); above that the
    // eruption picks seeded-uniformly among the N deepest of the pool it was
    // handed. The pool arrives already filtered for wall-eligibility, so the
    // pincer's opposite-wall convergence and every cap/reserve rule still
    // decide WHICH cells are candidates — this only varies which of the
    // deepest candidates wins, which is the half of O-5's "predictable" that
    // lives in placement rather than timing.
    const depth = this.policy.spawnPickSeededDepth ?? 0;
    if (depth > 1 && ranked.length > 1) {
      const span = Math.min(depth, ranked.length);
      return ranked[Math.min(span - 1, Math.floor(this.rng() * span))]!;
    }
    return ranked[0]!;
  }

  /** AC-10 / OQ-3 — the per-run jittered scripted ladder. Computed once per run
   *  (on {@link init}/{@link reset}) from the run's isolated breach stream, so
   *  every wave-membership test below reads a stable, seed-reproducible list
   *  rather than re-drawing per query. */
  private jitterLadder(waves: readonly number[]): readonly number[] {
    const jitter = this.policy.scriptedBreachJitter ?? 0;
    if (jitter <= 0) return waves;
    const moved = waves.map((w) => {
      // Uniform integer offset in [-jitter, +jitter], then clamped into the
      // ACT the authored wave belongs to.
      //
      // A bare "never below Act I" floor is NOT enough, and assuming it was is
      // exactly the kind of thing the breach-witness assertion caught: with
      // jitter 3 the Act III pincer (W26) would reach W23, landing it in Act II
      // and destroying the "Act I clean -> Act II flank -> Act III converging
      // pincer" choreography the run is built around. Clamping per act keeps
      // WHEN a breach lands unpredictable while keeping WHICH ACT it belongs
      // to fixed, which is the part that carries story and difficulty meaning.
      // FORWARD-ONLY, and the authored wave is the floor.
      //
      // The first version jittered symmetrically and clamped into the act. Two
      // things were wrong with that, both measured rather than argued:
      //
      //  - It made the arc HARDER than the ratified baseline in 44% of runs.
      //    An earlier flank means an extra active lane sooner, and a lane is a
      //    full volume multiplier (measured at W16: 376 spawns on one lane, 738
      //    on two). Opening W15's flank at W13 doubles two waves that the
      //    economy was never tuned to absorb. No ratified target sanctioned
      //    that, and D-305 explicitly stands untouched.
      //  - The clamp PILED UP on the band's first wave: every draw that would
      //    have landed below it collapsed onto it, so W13 alone took 31% of
      //    runs — three times any other wave. A distribution whose most likely
      //    outcome is an artifact of its own clamp is not "unpredictable", it
      //    is differently predictable.
      //
      // Forward-only fixes both: the authored wave stays the earliest possible
      // one (so difficulty never exceeds the ratified baseline), the pile-up
      // moves to the authored wave where it is honest rather than surprising,
      // and O-5's Q-d complaint is still answered — the flank no longer lands
      // on the same wave every run.
      const [, hi] = actBandOf(w);
      const offset = Math.floor(this.rng() * (jitter + 1));
      return Math.min(hi, w + offset);
    });
    // Collisions would silently drop an eruption from the ladder (membership is
    // tested with includes), so push duplicates forward to keep the COUNT of
    // scripted breaches per run invariant — the jitter moves when they land,
    // never how many land.
    const seen = new Set<number>();
    return moved
      .slice()
      .sort((a, b) => a - b)
      .map((w) => {
        const [lo, hi] = actBandOf(w);
        let v = w;
        // Resolve forward inside the act, then backward if the act's top is
        // full — never out of the band, which would undo the clamp above.
        while (seen.has(v) && v < hi) v++;
        while (seen.has(v) && v > lo) v--;
        seen.add(v);
        return v;
      });
  }

  /** AC-10 / OQ-3 — this run's actual scripted ladder and pincer waves, after
   *  the seeded jitter. Exposed because with the jitter on, the authored
   *  constants no longer tell you when a run's flank opens: anything asserting
   *  the choreography (the breach witness) has to read the ladder the run
   *  actually drew, not `DEFAULT_SPAWN_POLICY`'s literals. */
  get ladder(): { scripted: readonly number[]; pincer: readonly number[] } {
    return { scripted: this.scriptedWaves, pincer: this.pincerWaves };
  }

  getDebugState(): {
    totalSpawns: number;
    activeSpawns: number;
    availableSpawns: number;
    lastBreachWave: number;
  } {
    const activeSpawns = this.dormantSpawns.filter((s) => s.active).length;
    return {
      totalSpawns: this.dormantSpawns.length,
      activeSpawns,
      availableSpawns: this.dormantSpawns.length - activeSpawns,
      lastBreachWave: this.lastBreachWave,
    };
  }

  /** Execute a breach: activate spawn, compute path, find towers to destroy.
   *
   *  LD-22.2-1: Architecture split between INITIAL CARVE and FUTURE RECALCS.
   *  - Initial path computation uses PENALTY grid so the breach can "carve
   *    through" the current tower wall (penalty 5× makes it routable). The
   *    towers on that initial path are collected as `destroyedCells` and
   *    blown up by the cinematic — turning their cells into EMPTY.
   *  - The path is then REGISTERED with a BLOCKING grid (towers fully
   *    block, same semantics as primary). All future `recalculateAll` runs
   *    (triggered by tower/firewall placement) use blocking, so enemies
   *    never walk through towers after the breach. canPlace already
   *    iterates `grid.getEntries()` (which includes the new PATH_ENTRY
   *    after `setCell` below) with blocking semantics, so the player
   *    cannot place a tower that would isolate the breach path. */
  executeBreach(wave: number): BreachResult | null {
    // C2: cap reached → no eruption. Spawns never deactivate, so a capped
    // breach can't resolve later — do NOT set a deferred flag (that looped the
    // banner forever). `hasAvailableSpawn` is also cap-gated, so callers won't
    // normally reach here; this stays as a pure safety.
    if (this.getActiveSpawns().length >= this.policy.maxActiveSpawns) {
      return null;
    }

    // BL-146: side-aware pick — prefer the wall opposite the last eruption.
    const spawn = this.pickNextSpawn();
    if (!spawn) return null;

    const exits = this.grid.getExits();
    if (exits.length === 0) return null;
    const exit = exits[0]!;
    const start = { x: spawn.col, y: spawn.row };

    // H1: compute the route BEFORE mutating any state, so a null-path breach
    // can never strand an orphan PATH_ENTRY (which would soft-brick all future
    // placement). Treat the spawn cell as walkable for this computation — it is
    // about to become an entry and any tower on it erupts — WITHOUT touching the
    // real grid. This also preserves the prefer-clean behaviour: a trap with a
    // tower on it can still take a clean detour (destroying only the on-spawn
    // tower) instead of always carving.
    const asStartWalkable = (g: PathGrid): PathGrid => {
      const isBlocked = (gx: number, gy: number): boolean =>
        gx === start.x && gy === start.y ? false : g.isBlocked(gx, gy);
      return g.getCost
        ? { width: g.width, height: g.height, isBlocked, getCost: g.getCost }
        : { width: g.width, height: g.height, isBlocked };
    };

    // LD-22.3-1: Prefer-clean breach routing — try BLOCKING first (clean route
    // around towers), fall back to PENALTY carve only if walled off.
    const blockingGrid = this.createBlockingGrid();
    let path = this.pathfinding.findPathUncached(
      asStartWalkable(blockingGrid),
      start,
      exit,
    );
    let usedCarve = false;
    if (!path) {
      const carvingGrid = this.createPenaltyGrid();
      path = this.pathfinding.findPathUncached(
        asStartWalkable(carvingGrid),
        start,
        exit,
      );
      usedCarve = true;
    }
    if (!path) return null; // no route — abort with ZERO side effects (H1)

    // Detect a tower/wall sitting on the spawn cell BEFORE converting it.
    const spawnCell = this.grid.getCell(spawn.col, spawn.row);
    const occupiedSpawn =
      spawnCell &&
      (spawnCell.type === CellType.TOWER || spawnCell.type === CellType.WALL)
        ? { x: spawn.col, y: spawn.row }
        : null;

    // ── Commit (only now that a route is confirmed) ──
    this.lastBreachWave = wave;
    this.deferredBreachPending = false;
    spawn.active = true;
    this.lastEruptedWall = this.wallOf(spawn); // BL-146: drive side-selection
    this.grid.setCell(spawn.col, spawn.row, CellType.PATH_ENTRY);

    // LD-22.2-1: register with the BLOCKING grid (spawn cell is now PATH_ENTRY,
    // blocksPath=false) for all future recalcs.
    const breachId = `breach:${spawn.col},${spawn.row}`;
    this.pathRegistry?.register(breachId, start, exit, blockingGrid);

    // The on-spawn tower (if any) always erupts; carve-path towers only when carved.
    const destroyedCells: Vec2[] = occupiedSpawn ? [occupiedSpawn] : [];
    if (usedCarve) {
      for (const p of path) {
        const cell = this.grid.getCell(p.x, p.y);
        if (
          cell &&
          (cell.type === CellType.TOWER || cell.type === CellType.WALL) &&
          !destroyedCells.some((dc) => dc.x === p.x && dc.y === p.y)
        ) {
          destroyedCells.push(p);
        }
      }
    }

    return { spawn, path, destroyedCells };
  }

  /** BL-146 / D-248 — the Act III pincer: erupt up to {@link
   *  SpawnPolicy.pincerCount} spawns at once. Because {@link executeBreach}
   *  prefers the wall opposite the previous eruption, the two breaches converge
   *  from DIFFERENT walls on the core ("she breaches the walls"). Each returned
   *  result is telegraphed + cinematically revealed by the caller exactly like a
   *  single breach. Marks the pincer as fired so the reserve is released. */
  executePincer(wave: number): BreachResult[] {
    this.pincerFired = true;
    const results: BreachResult[] = [];
    for (let i = 0; i < this.policy.pincerCount; i++) {
      if (!this.hasAvailableSpawn(true)) break;
      const result = this.executeBreach(wave);
      if (!result) break;
      results.push(result);
    }
    return results;
  }

  /** Create a PathGrid where towers have higher cost (penalty-based routing).
   *  Used ONLY for the INITIAL breach-carve computation in executeBreach —
   *  finds a route the cinematic will then blow open. Not used for any
   *  subsequent recalculate (see createBlockingGrid). */
  private createPenaltyGrid(): PathGrid {
    return {
      width: this.grid.width,
      height: this.grid.height,
      isBlocked: (gx: number, gy: number) => {
        const cell = this.grid.getCell(gx, gy);
        if (!cell) return true;
        // Only truly block OBSTACLE and BLOCKER
        if (cell.type === CellType.OBSTACLE || cell.type === CellType.BLOCKER) {
          return true;
        }
        return false;
      },
      // Penalty cost: towers/walls cost 5x but are traversable for breach path
      getCost: (gx: number, gy: number) => {
        const cell = this.grid.getCell(gx, gy);
        if (
          cell &&
          (cell.type === CellType.TOWER || cell.type === CellType.WALL)
        ) {
          return 5;
        }
        return 1;
      },
    };
  }

  /** LD-22.2-1: PathGrid registered with PathRegistry for breach paths.
   *  Tower + Wall fully block, same semantics as the primary path grid in
   *  createGrid.ts + the canPlace blocking-grid check in inputHandlers.
   *  Guarantees that post-breach enemies follow only clean routes and
   *  never walk through player-built towers (the user's bug 1/3/4). */
  private createBlockingGrid(): PathGrid {
    return {
      width: this.grid.width,
      height: this.grid.height,
      isBlocked: (gx: number, gy: number) => {
        const cell = this.grid.getCell(gx, gy);
        // Mirror cell.blocksPath (the same field canPlace uses) — primary
        // path semantics. Falls back to `true` for out-of-bounds.
        return cell?.blocksPath ?? true;
      },
    };
  }

  /** Get all active spawn positions (for multi-path) */
  getActiveSpawns(): Vec2[] {
    return this.dormantSpawns
      .filter((s) => s.active)
      .map((s) => ({ x: s.col, y: s.row }));
  }
}
